/**
 * รูปสินค้า (Product Images) — รูปที่บอตส่งให้ลูกค้า (ขอดูรูป / ตารางไซส์ / รูปแยกสี)
 * เชื่อมกับ: สี (color = color_key) · Triggers (ขอดูรูป/ตารางไซส์) · ราคา (itemKey)
 *
 * ใช้ CrudManager กลาง — แนบรูป (ImageAttach) + context/สี + ดึงรูปจาก FB + AI ตรวจสี (headerExtra)
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CrudManager, type CrudField } from '@/components/CrudManager';
import { ImagesApi, type ProductImageItem, type Brand } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const CONTEXTS = [
  { value: 'main', label: 'รูปหลัก (main)' },
  { value: 'color_variant', label: 'รูปแยกสี (color_variant)' },
  { value: 'size_chart', label: 'ตารางไซส์ (size_chart)' },
  { value: 'detail', label: 'รายละเอียด (detail)' },
  { value: 'banner', label: 'แบนเนอร์ (banner)' },
  { value: 'lifestyle', label: 'ไลฟ์สไตล์ (lifestyle)' },
];
const ctxLabel = (v: string) => CONTEXTS.find((c) => c.value === v)?.label ?? v;

const Api = {
  list: (brand?: Brand) => ImagesApi.list(brand ? { brand } : {}),
  create: ImagesApi.create,
  update: ImagesApi.update,
  remove: ImagesApi.remove,
};

const FIELDS: CrudField[] = [
  { key: 'imageUrl', label: 'รูป', type: 'image', required: true, help: 'อัปโหลดหรือวางลิงก์รูป' },
  { key: 'context', label: 'ใช้ตอนไหน (context)', type: 'select', required: true, options: CONTEXTS, default: 'main', help: 'บอตเลือกส่งรูปตาม context นี้ (ขอดูรูป=main, ถามไซส์=size_chart)' },
  { key: 'color', label: 'สี (color_key)', type: 'text', placeholder: 'green', help: 'ตรงกับรหัสสีในเมนู “สี” (เฉพาะ context=รูปแยกสี)' },
  { key: 'itemKey', label: 'ผูกสินค้า (item_key)', type: 'text', help: 'ตรงกับรหัสในเมนู “ราคา” (ไม่บังคับ)' },
  { key: 'altText', label: 'คำอธิบายรูป', type: 'textarea', help: 'บันทึกภายใน (ไม่บังคับ)' },
  { key: 'sortOrder', label: 'ลำดับ', type: 'number', default: 0, help: 'เลขน้อยขึ้นก่อน' },
  { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', default: true },
];

export default function ImagesPage() {
  return (
    <CrudManager<ProductImageItem>
      title="🖼️ รูปสินค้า (Product Images)"
      addLabel="เพิ่มรูป"
      help="รูปที่บอตส่งให้ลูกค้า. ขอดูรูป → ส่ง context=main (ถ้าระบุสี ส่งรูปสีนั้น) · ถามไซส์ → ส่ง size_chart. ผูกสีให้ตรงรหัสในเมนู “สี”"
      queryKey="product-images"
      api={Api}
      fields={FIELDS}
      brandScoped
      searchKeys={['context', 'color', 'itemKey', 'altText']}
      groupBy={(im) => ctxLabel(im.context)}
      sortRows={(a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id}
      rowTitle={(im) => <>{im.color ? `สี ${im.color}` : ctxLabel(im.context)}{im.itemKey ? ` · ${im.itemKey}` : ''}</>}
      rowSubtitle={(im) => (
        <span className="flex items-center gap-2">
          <img src={ImagesApi.previewSrc(im)} alt={im.altText ?? ''} className="w-12 h-12 object-cover rounded border" onError={(e) => ((e.target as HTMLImageElement).style.opacity = '0.2')} />
          <span className="truncate max-w-md">{im.altText || im.imageUrl}</span>
        </span>
      )}
      headerExtra={(brand) => <FBSyncPanel brand={brand} />}
    />
  );
}

// ── ดึงรูปจากเพจ Facebook + AI ตรวจสี → เพิ่มเข้าคลังรูป ──
type FbPhoto = { fbid: string; name: string; permalink: string; source: string; width: number; height: number; createdTime: string };
function FBSyncPanel({ brand }: { brand: Brand }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState<FbPhoto[]>([]);
  const [drafts, setDrafts] = useState<Record<string, { context: string; color: string }>>({});

  const syncMut = useMutation({
    mutationFn: () => ImagesApi.syncFromFb(brand, 50),
    onSuccess: (r) => { setPhotos(r.photos); setOpen(true); toast.success(`ดึงรูปจาก ${r.pageName} ได้ ${r.count} รูป`); },
    onError: (e: Error) => toast.error('ดึงรูปไม่ได้: ' + e.message),
  });
  const classifyMut = useMutation({
    mutationFn: (url: string) => ImagesApi.classifyFb(url),
  });
  const addMut = useMutation({
    mutationFn: (p: FbPhoto) => ImagesApi.create({ brand, imageUrl: p.source, context: (drafts[p.fbid]?.context ?? 'main') as ProductImageItem['context'], color: drafts[p.fbid]?.color || null, altText: p.name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['product-images', brand] }); toast.success('เพิ่มรูปแล้ว'); },
    onError: (e: Error) => toast.error('เพิ่มไม่ได้: ' + e.message),
  });
  const setDraft = (id: string, patch: Partial<{ context: string; color: string }>) =>
    setDrafts((d) => ({ ...d, [id]: { context: 'main', color: '', ...d[id], ...patch } }));

  return (
    <div className="border rounded-lg p-3 bg-muted/20 space-y-2">
      <Button size="sm" variant="outline" onClick={() => (photos.length ? setOpen((v) => !v) : syncMut.mutate())} disabled={syncMut.isPending}>
        {syncMut.isPending ? 'กำลังดึง…' : '🔄 ดึงรูปจากเพจ Facebook'} {photos.length > 0 && (open ? '▲' : '▼')}
      </Button>
      {open && photos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
          {photos.map((p) => {
            const d = drafts[p.fbid] ?? { context: 'main', color: '' };
            return (
              <div key={p.fbid} className="border rounded-lg p-2 space-y-1 bg-card">
                <img src={p.source} alt={p.name} className="w-full h-28 object-cover rounded" onError={(e) => ((e.target as HTMLImageElement).style.opacity = '0.2')} />
                <select className="w-full border rounded px-1 py-1 text-xs" value={d.context} onChange={(e) => setDraft(p.fbid, { context: e.target.value })}>
                  {CONTEXTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <div className="flex gap-1">
                  <Input className="h-7 text-xs" placeholder="สี (เช่น green)" value={d.color} onChange={(e) => setDraft(p.fbid, { color: e.target.value })} />
                  <Button size="sm" variant="outline" className="h-7 px-1.5 text-[11px]" title="AI ตรวจสีจากรูป"
                    onClick={() => classifyMut.mutate(p.source, { onSuccess: (r) => { setDraft(p.fbid, { color: r.color }); toast.success(`AI: ${r.is_scrub ? 'ชุดสครับ' : 'ไม่ใช่ชุด'} สี ${r.color} (${r.confidence})`); } })}>
                    🤖
                  </Button>
                </div>
                <Button size="sm" className="w-full h-7 text-[11px]" onClick={() => addMut.mutate(p)}>+ เพิ่มเข้าคลังรูป</Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
